import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import classnames from 'classnames';
import { addConfig, addContext, configConsumer, translate } from '@estee/elc-service';
import { breakpoint } from '@estee/elc-base-theme';
import { Spinner } from '@estee/elc-spinner';
import { classes } from '~utils/Classes';
import { VtoViewController } from '~controllers/VtoViewController';
import { IVtoServiceConfig } from '~interfaces/IVto';
import { VtoShadeGrids } from '~views/VtoShadeGrids';
import { VtoFoundationContentBlock } from '~views/VtoFoundationContentBlock';
import { SupportedBrowserOverlay } from '~views/common/supported-browser-overlay/SupportedBrowserOverlay';
import { VtoFoundationQrCodeOverlay } from '~views/vto-foundation/VtoFoundationQrCodeOverlay';
import { VtoSurvey } from '~views/vto-survey/VtoSurvey';

export interface IVto {
    config: IVtoServiceConfig;
    translations: IVtoTranslations;
    viewController: VtoViewController;
}

export interface IVtoTranslations {
    coolerLabel: string;
    lighterLabel: string;
    bestLabel: string;
    darkerLabel: string;
    warmerLabel: string;
    coolerPairLabel: string;
    casualPairLabel: string;
    bestPairLabel: string;
    glamPairLabel: string;
    warmerPairLabel: string;
}

const IframeContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
    width: 100%;
    height: 100%;
    display: none;
    ${breakpoint('desktop')`
        position: static;
        width: auto;
    `}
    [dir='rtl'] & {
        right: 0;
    }
    &.elc-vto-iframe-container-opened {
        display: block;
    }
`;

const Iframe = styled.div`
    & > div {
        ${breakpoint('desktop')`
            margin: 0 auto;
        `}
    }
`;

const iframeClassNames = classes('vto-iframe');

@addContext()
@addConfig(['contentServiceNodeIds', 'vtoFeature', 'hideProductPricePerUnit'])
@configConsumer(['contentServiceNodeIds'])
@translate([
    'coolerLabel',
    'lighterLabel',
    'bestLabel',
    'darkerLabel',
    'warmerLabel',
    'coolerPairLabel',
    'casualPairLabel',
    'bestPairLabel',
    'glamPairLabel',
    'warmerPairLabel'
])
@observer
class Vto extends React.Component<IVto> {
    private vtoIframeRef: React.RefObject<HTMLDivElement>;

    constructor(props: IVto) {
        super(props);
        const { viewController, ...restProps } = props;
        const vtoProps = this.vtoProps(restProps);
        props.viewController.initialize(vtoProps);
    }

    public componentDidUpdate(prevProps: IVto) {
        const {
            viewController,
            config: { selectedSku }
        } = this.props;

        if (selectedSku !== prevProps.config.selectedSku) {
            viewController.updateProductData(selectedSku);
        }
    }

    public componentWillUnmount() {
        this.props.viewController.handleVtoUnmount();
    }

    public async componentDidMount() {
        this.props.viewController.checkHidePriceOnLoad();
    }

    private vtoProps = (props: Omit<IVto, 'viewController'>) => {
        this.vtoIframeRef = React.createRef();

        return {
            ...props,
            vtoIframeRef: this.vtoIframeRef
        };
    };

    private getVtoFoundationOverlayNodeId = () => {
        const { vtoConfig } = this.props.config;

        return (
            vtoConfig.contentServiceNodeIds && vtoConfig.contentServiceNodeIds.vtoFoundationOverlay
        );
    };

    private trackCloseSupportedBrowserOverlay = () => {
        const {
            viewController: { closeSupportedBrowserOverlay, getVtoType }
        } = this.props;

        closeSupportedBrowserOverlay(getVtoType());
    };

    public render() {
        const { viewController, config } = this.props;
        const {
            data: {
                isVtoOpened,
                isVtoShadeGridEnabled,
                isVtoFoundationOverlayOpened,
                isMatchSaved,
                isSaveInProgress,
                isVtoPriceHidden,
                isSpinnerEnabled,
                isSupportedBrowserOverlayVisible,
                isSupportedBrowserOverlayOpen,
                isVtoFoundationQrCodeOverlayEnabled,
                isSurveyVisible,
                showThankMessage,
                surveyButtonIndex,
                surveyComment
            },
            removeMatches,
            saveMatches,
            sortPerfectLabels,
            slidesToShow,
            slidesToScroll,
            handleShadeSelect,
            submitSurveyData,
            closeSurvey,
            setThankMessageStatus,
            setSurveyButtonIndex,
            handleSurveySubmit,
            setSurveyComment
        } = viewController;
        const {
            isVtoFoundationEnabled,
            isVtoMakeupEnabled,
            selectedSku,
            isPerfectShadeEnabled,
            togglePerfectShades,
            largeImages,
            selectedAutoReplenishOption
        } = config;

        const vtoShadeGridProps = {
            productShadePickerProps: {
                selectedSku,
                isPerfectShadeEnabled,
                sortPerfectLabels,
                onShadeSelect: handleShadeSelect,
                slidesToShow: slidesToShow,
                slidesToScroll: slidesToScroll
            },
            togglePerfectShades,
            largeImages,
            selectedAutoReplenishOption
        };

        const vtoContainerClassNames = classnames({
            [classes('vto-foundation')]: isVtoFoundationEnabled,
            [classes('vto-makeup')]: isVtoMakeupEnabled
        });

        const vtoIframeClassNames = classes('vto-iframe-container', {
            'elc-vto-iframe-container-opened': isVtoOpened
        });

        if (isSupportedBrowserOverlayVisible) {
            return (
                <SupportedBrowserOverlay
                    closeOverlay={this.trackCloseSupportedBrowserOverlay}
                    isOpen={isSupportedBrowserOverlayOpen}
                />
            );
        }

        return (
            <div className={vtoContainerClassNames}>
                <IframeContainer className={vtoIframeClassNames}>
                    {isSpinnerEnabled && <Spinner className="elc-vto-spinner" isLoading />}

                    {isVtoFoundationQrCodeOverlayEnabled ? (
                        <VtoFoundationQrCodeOverlay />
                    ) : (
                        <Iframe
                            id="YMK-module"
                            ref={this.vtoIframeRef}
                            className={iframeClassNames}
                        />
                    )}

                    <VtoFoundationContentBlock
                        isVtoFoundationEnabled={isVtoFoundationEnabled}
                        isOverlayOpened={isVtoFoundationOverlayOpened}
                        overlayNodeId={this.getVtoFoundationOverlayNodeId()}
                    />
                    <VtoShadeGrids
                        shadeGridProps={vtoShadeGridProps}
                        isVtoShadeGridEnabled={isVtoShadeGridEnabled}
                        isMatchSaved={isMatchSaved}
                        isSaveInProgress={isSaveInProgress}
                        isVtoPriceHidden={isVtoPriceHidden}
                        removeMatches={removeMatches}
                        saveMatches={saveMatches}
                        viewController={viewController}
                    />
                </IframeContainer>
                {isSurveyVisible && (
                    <VtoSurvey
                        submitSurveyData={submitSurveyData}
                        closeSurvey={closeSurvey}
                        showThankMessage={showThankMessage}
                        setThankMessageStatus={setThankMessageStatus}
                        surveyButtonIndex={surveyButtonIndex}
                        setSurveyButtonIndex={setSurveyButtonIndex}
                        handleSurveySubmit={handleSurveySubmit}
                        surveyComment={surveyComment}
                        setSurveyComment={setSurveyComment}
                    />
                )}
            </div>
        );
    }
}

export default Vto;
